import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { MaintenanceSchedulesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { MaintenanceScheduleFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { EditDialog } from './edit_dialog'
import { pluralize } from '@/helpers/pluralize'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<MaintenanceSchedulesQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const {
    pageInfo,
    setOpenNew,
    openShow,
    openNew,
    showShow,
    hideShow,
    resourceId,
  } = useTable({ pageInfo: data?.maintenanceSchedules.pageInfo, onIndex, basePath: '/settings/maintenance-schedules' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<MaintenanceScheduleFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }, { name: 'Period' }, { name: 'Details' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.maintenanceSchedules.nodes = dedupe([
              ...previousData.maintenanceSchedules.nodes,
              ...fetchMoreResult.maintenanceSchedules.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        rows={data.maintenanceSchedules.nodes.map(ms => (
          {
            id: ms.id,
            columns: [
              ms.maintenanceType.name,
              pluralize(ms.repeatPeriod, ms.repeatMultiplier, true),
              ms.details
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <EditDialog id={resourceId} open={openShow} setOpen={hideShow} /> }

      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
